import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Link,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import buildingImg from "@/assets/building.png";
import SettingsModal from "@/components/SettingsModal";
import {
  DesktopPortalPreview,
  MobilePortalPreview,
  PREVIEW_FRAME_WIDTH,
  PREVIEW_HEIGHT,
  PreviewMode,
  PreviewToggle,
} from "@/components/settings/PortalPreview";
import { useMediaBust, withCacheBust } from "@/helpers/mediaUrl";
import { useMerchant } from "@/hooks/useMerchant";
import { usePermissions } from "@/hooks/usePermissions";

type Props = { open: boolean; onClose: () => void };

const TenantPreviewModal = ({ open, onClose }: Props): JSX.Element => {
  const { merchant } = useMerchant();
  const { isMerchantAdmin } = usePermissions();
  const mediaBust = useMediaBust();
  const [mode, setMode] = useState<PreviewMode>("mobile");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const logoUrl = withCacheBust(merchant?.logoUrl ?? "", mediaBust);
  const imageSrc =
    withCacheBust(merchant?.dashboardImageUrl ?? "", mediaBust) || buildingImg;

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content borderRadius="md" maxWidth="560px">
              <Dialog.Header>
                <Box>
                  <Dialog.Title fontSize="lg" fontWeight="semibold">
                    Así lo ven tus inquilinos
                  </Dialog.Title>
                  <Text fontSize="sm" color="gray.500">
                    Entran con su cédula en {merchant?.paymentsUrl} y pagan
                    desde el celular o el computador.
                  </Text>
                </Box>
              </Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <PreviewToggle value={mode} onChange={setMode} />
                  <HStack justify="center">
                    <Box
                      width={`${PREVIEW_FRAME_WIDTH}px`}
                      maxWidth="100%"
                      height={`${PREVIEW_HEIGHT}px`}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {mode === "mobile" ? (
                        <MobilePortalPreview
                          logoUrl={logoUrl || undefined}
                          imageSrc={imageSrc}
                        />
                      ) : (
                        <DesktopPortalPreview
                          logoUrl={logoUrl || undefined}
                          imageSrc={imageSrc}
                        />
                      )}
                    </Box>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Si quieres cambiar la imagen o el logo del portal,{" "}
                    {isMerchantAdmin ? (
                      <>
                        ve a{" "}
                        <Link
                          as="button"
                          color="palomma.700"
                          textDecoration="underline"
                          textUnderlineOffset="4px"
                          onClick={() => setSettingsOpen(true)}
                        >
                          Configuración
                        </Link>
                        .
                      </>
                    ) : (
                      "pídele a un administrador del comercio que lo haga desde Configuración."
                    )}
                  </Text>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button colorPalette="palomma" onClick={onClose}>
                  Entendido
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
      {isMerchantAdmin && (
        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );
};

export default TenantPreviewModal;
